import {
  AssetReviewRequest,
  GetAssetReviewRequestsParams,
} from "Interfaces/Pages/AssetReviewRequestsInterface";
import type { ReactNode } from "react";

export const ASSET_REVIEW_REQUESTS_TITLE = "Review Requests";
export const STATUS_TABS = [
  { label: "Pending", value: "pending" },
  { label: "Awaiting Coach", value: "awaiting_coach" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
] as const;

type AugmentedReviewRequestRow = AssetReviewRequest & {
  _bulk?: { checked: boolean };
};

type TableColumn<Row> = {
  dataField?: string;
  title: string;
  colWidth?: string;
  formatter?: (row: Row) => ReactNode;
};

export const getAssetReviewRequestColumns = (options?: {
  status?: GetAssetReviewRequestsParams["status"];
  onReassignCoach?: (
    reviewRequestId: number,
    masterSportId: number,
    currentCoachId?: number
  ) => void;
  isBulkMode?: boolean;
  onToggleRow?: (id: number, checked: boolean) => void;
}): TableColumn<AugmentedReviewRequestRow>[] => [
  // 1) Asset
  {
    title: "Asset",
    colWidth: "18%",
    formatter: (row: AssetReviewRequest) => {
      const { asset } = row;
      if (!asset) return "-";

      const gccId = asset.gcc_id || asset.kpoint_gcc_id;
      const hasGcc = typeof gccId === "string" && gccId.trim().length > 0;
      const videoUrl = hasGcc
        ? `${process.env.REACT_APP_KPOINT_API_HOST}web/videos/${gccId}`
        : undefined;
      const isActive = asset.is_active;
      const defaultThumb =
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='60'><rect width='80' height='60' fill='%23eee'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='10'>No Image</text></svg>";

      return (
        <div className="d-flex flex-column align-items-start">
          <div className="thumbnail-box position-relative mb-2">
            {isActive && hasGcc ? (
              <a href={videoUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={asset.thumbnail_url || defaultThumb}
                  alt=""
                  height="60px"
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    if (img.src !== defaultThumb) {
                      img.onerror = null;
                      img.src = defaultThumb;
                    }
                  }}
                />
              </a>
            ) : (
              <img
                src={asset.thumbnail_url || defaultThumb}
                alt=""
                height="60px"
                style={{ filter: !isActive ? "grayscale(100%)" : undefined }}
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  if (img.src !== defaultThumb) {
                    img.onerror = null;
                    img.src = defaultThumb;
                  }
                }}
              />
            )}
          </div>
          {isActive && hasGcc ? (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "blue", textDecoration: "none" }}
            >
              {asset.master_sport_name ? (
                <>
                  <span
                    className="text-muted"
                    style={{ fontSize: 12, fontWeight: "normal" }}
                  >
                    {asset.master_sport_name}
                  </span>
                  <span
                    className="text-muted"
                    style={{ fontSize: 12, fontWeight: "normal" }}
                  >
                    {" - "}
                  </span>
                  <span>{asset.title}</span>
                </>
              ) : (
                asset.title
              )}
            </a>
          ) : (
            <div className="d-flex flex-column">
              <span style={{ color: "black" }}>
                {asset.master_sport_name ? (
                  <>
                    <span
                      className="text-muted"
                      style={{ fontSize: 12, fontWeight: "normal" }}
                    >
                      {asset.master_sport_name}
                    </span>
                    <span
                      className="text-muted"
                      style={{ fontSize: 12, fontWeight: "normal" }}
                    >
                      {" - "}
                    </span>
                    <span>{asset.title || "-"}</span>
                  </>
                ) : (
                  asset.title || "-"
                )}
              </span>
              {!isActive && (
                <span style={{ color: "red", fontSize: "12px" }}>Deleted</span>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  // 2) Created At
  {
    dataField: "created_at",
    title: "Created At",
    colWidth: "16%",
  },
  // 3) TAT (pending). For completed, we'll add Payment later after Coach
  ...(options?.status === "pending"
    ? [
        {
          title: "TAT Status",
          colWidth: "14%",
          formatter: (row: AssetReviewRequest) => {
            const n = row.days_remaining_for_tat;
            if (typeof n !== "number") return "-";

            let label = "";
            let bg = "#e6f4ea";

            if (n < 0) {
              label = "Overdue";
              bg = "#fbeaea";
            } else if (n === 0) {
              label = "Today";
              bg = "#fff7e6";
            } else {
              label = `${n} Days`;
              bg = "#e6f4ea";
            }

            return (
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: bg,
                  color: "#000",
                  borderRadius: "9999px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  lineHeight: 1.5,
                }}
              >
                {label}
              </span>
            );
          },
        },
      ]
    : []),
  // We'll add Player Rating and Payment later for completed (after Coach)
  // 4) Requestor
  {
    title: "Requestor",
    colWidth: "16%",
    formatter: (row: AssetReviewRequest) => row.requestor_name || "-",
  },
  // 5) Review Rate
  ...(options?.status !== "completed"
    ? [
        {
          title: "Review Rate",
          colWidth: "14%",
          formatter: (row: AssetReviewRequest) => {
            const { review_rate_currency, review_rate_value } = row;
            if (!review_rate_currency || review_rate_value == null) return "-";
            return `${review_rate_currency} ${review_rate_value}`;
          },
        },
      ]
    : []),
  // 6) Coach
  {
    title: "Coach",
    colWidth: "16%",
    formatter: (row: AssetReviewRequest) => {
      const coachName = row.coach?.name || "-";
      const coachId = row.coach?.id;
      const isPending = options?.status === "pending";
      const isAwaitingCoach = options?.status === "awaiting_coach";
      const isInProgress = options?.status === "in_progress";
      const masterSportId = row.asset?.master_sport_id as number | undefined;

      // Default: just show coach name or '-'
      const showReassign =
        !!options?.onReassignCoach &&
        (isPending || isAwaitingCoach || (isInProgress && !coachId));
      if (!showReassign) {
        return coachName;
      }

      return (
        <div className="d-flex align-items-center justify-content-between">
          {coachName !== "-" && <span>{coachName}</span>}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              options.onReassignCoach?.(row.id, masterSportId || 0, coachId);
            }}
            style={{
              fontSize: "12px",
              padding: "2px 8px",
              lineHeight: 1.2,
              border: "1px solid #0d6efd",
              boxSizing: "border-box",
              borderRadius: "4px",
              background: "transparent",
            }}
          >
            Reassign
          </button>
        </div>
      );
    },
  },
  // (checkbox column will be appended at the end)
  // For completed: after Coach, show Player Rating then Payment Status
  ...(options?.status === "completed"
    ? [
        {
          title: "Player Rating",
          colWidth: "18%",
          formatter: (row: AssetReviewRequest) => {
            const rating = row.requestor_rating_points;
            if (rating == null) return "-";
            const max = 5;
            const filled = Math.max(0, Math.min(max, Number(rating)));
            const stars = Array.from({ length: max }, (_, i) => (
              <span
                key={i}
                style={{
                  color: i < filled ? "#f1c40f" : "#e0e0e0",
                  fontSize: "18px",
                  lineHeight: 1,
                }}
              >
                ★
              </span>
            ));
            return <span style={{ color: "#000" }}>{stars}</span>;
          },
        },
        // Move Review Rate here (before Payment Status) for completed
        {
          title: "Review Rate",
          colWidth: "14%",
          formatter: (row: AssetReviewRequest) => {
            const { review_rate_currency, review_rate_value } = row;
            if (!review_rate_currency || review_rate_value == null) return "-";
            return `${review_rate_currency} ${review_rate_value}`;
          },
        },
      ]
    : []),
  ...(options?.status === "completed"
    ? [
        {
          title: "Payment Status",
          colWidth: "14%",
          formatter: (row: AssetReviewRequest) => {
            const paid = !!row.coach_paid_at;
            const label = paid ? "Paid" : "Unpaid";
            const bg = paid ? "#e6f4ea" : "#fff7e6";
            return (
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: bg,
                  color: "#000",
                  borderRadius: "9999px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  lineHeight: 1.5,
                }}
              >
                {label}
              </span>
            );
          },
        },
      ]
    : []),
  // 7) Bulk checkbox for completed bulk mode (reserved key _bulk) - always last
  ...(options?.status === "completed" && options?.isBulkMode
    ? [
        {
          title: "",
          colWidth: "8%",
          formatter: (row: AugmentedReviewRequestRow) => {
            const checked = row._bulk?.checked || false;
            return (
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) =>
                  options?.onToggleRow?.(row.id, e.target.checked)
                }
                onClick={(e) => e.stopPropagation()}
                style={{ width: 18, height: 18, cursor: "pointer" }}
              />
            );
          },
        },
      ]
    : []),
];
